import { useEffect } from "react";
import { ChevronRight } from "lucide-react";
import ServiceHeader from "@/components/ServiceHeader";
import Footer from "@/components/Footer";
import StickyCallButton from "@/components/StickyCallButton";
import BlogPostIcon from "@/components/BlogPostIcon";
import { postsNewestFirst } from "@/content/blogPosts";
import { upsertCanonical, upsertLdJson, upsertMeta } from "@/lib/head";

const SITE = "https://ozwasteco.com.au";
const TITLE = "Rubbish Removal Tips & Guides | OZ WASTE CO. Melbourne";
const DESCRIPTION =
  "Practical rubbish removal tips, clear-out checklists, cost guides and disposal advice from OZ WASTE CO., Melbourne's rubbish removal team.";
const URL = `${SITE}/blog`;

const glass =
  "bg-white/70 backdrop-blur-xl border border-white/60 shadow-[0_8px_30px_rgba(26,26,26,0.06)]";

const Blog = () => {
  const [featured, ...rest] = postsNewestFirst;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const cleanups = [
      upsertMeta("name", "description", DESCRIPTION),
      upsertMeta("property", "og:title", TITLE),
      upsertMeta("property", "og:description", DESCRIPTION),
      upsertMeta("property", "og:url", URL),
      upsertCanonical(URL),
      upsertLdJson("ld-blog", {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "OZ WASTE CO. Rubbish Removal Advice",
        description: DESCRIPTION,
        url: URL,
        inLanguage: "en-AU",
        publisher: { "@type": "Organization", name: "OZ WASTE CO.", url: `${SITE}/` },
        blogPost: postsNewestFirst.map((p) => ({
          "@type": "BlogPosting",
          headline: p.title,
          description: p.metaDescription,
          datePublished: p.date,
          url: `${SITE}/blog/${p.slug}`,
        })),
      }),
      upsertLdJson("ld-blog-index-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: URL },
        ],
      }),
    ];
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ServiceHeader />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#FDC00E]">
          <div
            className="absolute inset-0 bg-[radial-gradient(circle_at_18%_25%,rgba(255,255,255,0.6),transparent_58%)]"
            aria-hidden="true"
          />
          <div className="container relative py-16 md:py-24">
            <nav
              aria-label="Breadcrumb"
              className="mb-8 text-sm text-[#1a1a1a]/70 flex flex-wrap items-center gap-1"
            >
              <a href="/" className="hover:text-[#1a1a1a] font-semibold">Home</a>
              <ChevronRight className="w-4 h-4 opacity-50" aria-hidden="true" />
              <span className="text-[#1a1a1a] font-bold">Blog</span>
            </nav>
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-6xl font-black text-[#1a1a1a] leading-[1.05] tracking-tight">
                Rubbish Removal Tips &amp; Guides
              </h1>
              <p className="mt-6 text-lg md:text-2xl text-[#1a1a1a]/75 font-medium leading-relaxed">
                Practical advice to help you sort, price and clear rubbish anywhere across
                Melbourne.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a
                  href="/#quote"
                  className="bg-[#1a1a1a] text-white px-8 py-4 text-base md:text-lg font-bold rounded-xl hover:bg-orange transition-colors text-center"
                >
                  Get a Free Quote
                </a>
                <a
                  href="#latest"
                  className="inline-flex items-center justify-center gap-2 bg-white/60 backdrop-blur-xl border border-[#1a1a1a]/15 text-[#1a1a1a] px-8 py-4 text-base md:text-lg font-bold rounded-xl hover:bg-white/80 transition-colors"
                >
                  View Latest Articles
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Featured */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#1a1a1a]/70 mb-6">
              Featured Article
            </p>
            <a
              href={`/blog/${featured.slug}`}
              className={`${glass} group block rounded-[2rem] p-7 md:p-10 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_22px_55px_rgba(26,26,26,0.13)]`}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-7 md:gap-10">
                <BlogPostIcon icon={featured.icon} size="lg" />
                <div className="flex-1">
                  <span className="inline-block bg-[#1a1a1a] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                    {featured.category}
                  </span>
                  <h2 className="mt-4 text-2xl md:text-4xl font-black text-[#1a1a1a] leading-tight tracking-tight">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-[#1a1a1a]/75 text-base md:text-lg leading-relaxed max-w-2xl">
                    {featured.excerpt}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm font-semibold text-[#1a1a1a]/65">
                    <time dateTime={featured.date}>{featured.dateLabel}</time>
                    <span className="opacity-50">·</span>
                    <span>{featured.readingTime}</span>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-1 font-bold text-[#1a1a1a] underline decoration-[#FDC00E] decoration-2 underline-offset-4">
                    Read Article
                    <ChevronRight
                      className="w-4 h-4 transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </div>
              </div>
            </a>
          </div>
        </section>

        {/* Grid */}
        <section id="latest" className="scroll-mt-28 py-16 md:py-20 bg-[#fafafa]">
          <div className="container">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] tracking-tight">
              Latest Clear-Out Guides
            </h2>
            <p className="mt-3 text-[#1a1a1a]/70 text-base md:text-lg max-w-2xl leading-relaxed">
              Checklists, cost breakdowns and sorting advice written by the crews who clear rubbish across
              Melbourne every week.
            </p>

            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {postsNewestFirst.map((p) => (
                <a
                  key={p.slug}
                  href={`/blog/${p.slug}`}
                  className={`${glass} group rounded-3xl p-7 flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_45px_rgba(26,26,26,0.12)]`}
                >
                  <BlogPostIcon icon={p.icon} />
                  <p className="mt-6 text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/70">
                    {p.category}
                  </p>
                  <h3 className="mt-2 text-xl font-bold text-[#1a1a1a] leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm md:text-base text-[#1a1a1a]/70 leading-relaxed flex-1">
                    {p.excerpt}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold text-[#1a1a1a]/60">
                    <time dateTime={p.date}>{p.dateLabel}</time>
                    <span className="opacity-50">·</span>
                    <span>{p.readingTime}</span>
                  </div>
                  <span className="mt-4 text-sm font-bold text-[#1a1a1a] inline-flex items-center gap-1">
                    Read Article
                    <ChevronRight
                      className="w-4 h-4 transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </a>
              ))}
            </div>

            <p className="mt-12 text-[#1a1a1a]/70 text-base leading-relaxed max-w-2xl">
              Looking for a specific service? See{" "}
              <a href="/household-rubbish-removal" className="font-semibold underline decoration-[#FDC00E] decoration-2 underline-offset-4">household rubbish removal</a>,{" "}
              <a href="/furniture-removal" className="font-semibold underline decoration-[#FDC00E] decoration-2 underline-offset-4">furniture removal</a>,{" "}
              <a href="/commercial-rubbish-removal" className="font-semibold underline decoration-[#FDC00E] decoration-2 underline-offset-4">office &amp; commercial rubbish</a>,{" "}
              <a href="/garage-shed-clear-outs" className="font-semibold underline decoration-[#FDC00E] decoration-2 underline-offset-4">garage &amp; shed clear-outs</a> or{" "}
              <a href="/property-clear-outs" className="font-semibold underline decoration-[#FDC00E] decoration-2 underline-offset-4">property clear-outs</a>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-[#1a1a1a]">
          <div className="container max-w-3xl text-center">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight">
              Need rubbish gone?
            </h2>
            <p className="text-white/70 text-base md:text-lg mb-9 leading-relaxed">
              Send us a few photos and your access details and we will come back with a clear, written
              quote, with no hidden costs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#quote"
                className="bg-[#FDC00E] text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-xl hover:bg-orange transition-colors"
              >
                Get a Free Quote
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-xl border border-white/20 text-white px-8 py-4 text-lg font-bold rounded-xl hover:bg-white/20 transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default Blog;